import React from 'react';
import { xcss, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ConfigureSource } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/deprecations/ban-identifiers
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { createIssueTypeExperience } from '../../../../experiences.tsx';
import messages from '../messages.tsx';

const ISSUE_TYPE_COUNT_LIMIT = 30;

type Props = {
	configSource: ConfigureSource;
	onIssueTypeCreated: () => void;
	projectKey: string;
	setIsConfiguringIssueType: (isConfiguringIssueType: boolean) => void;
};

export const CreateIssueTypeButton = ({
	onIssueTypeCreated,
	projectKey,
	configSource,
	setIsConfiguringIssueType,
}: Props) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [inlineIssueTypeConfigProductionizationExpConfig] = UNSAFE_noExposureExp(
		'inline_issue_type_config_productionization_exp',
	);

	const handleCreateIssueTypeButtonClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'createIssueTypeButton',
		);
	};

	const handleCreateIssueTypeModalClose = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'inlineCreateIssueTypeModal',
				action: 'closed',
			}),
			{
				calledFrom: configSource,
			},
		);
		setIsConfiguringIssueType(false);
	};

	const entryPointProps = {
		onIssueTypeCreated,
		onClose: handleCreateIssueTypeModalClose,
		onOpen: () => setIsConfiguringIssueType(true),
		issueTypesCountLimit: ISSUE_TYPE_COUNT_LIMIT,
		ufoExperience: createIssueTypeExperience,
		source: configSource,
		showNewIssueTypeForm: true,
	};

	const entryPointParams = {
		projectKey,
		cloudId,
	};

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncCreateIssueTypeModalEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="create-issue-type-modal"
			modalProps={defaultModalProps}
			useInternalModal={inlineIssueTypeConfigProductionizationExpConfig.get('isEnabled', false)}
		>
			{({ ref }) => (
				<Pressable
					onClick={handleCreateIssueTypeButtonClick}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={ref as React.Ref<HTMLButtonElement>}
					xcss={footerButtonStyles}
				>
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? messages.addWorkTypeButton
							: messages.addIssueTypeButton,
					)}
				</Pressable>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const footerButtonStyles = xcss({
	display: 'flex',
	height: token('space.500'),
	width: '100%',
	color: 'color.text',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});
